import React, { useState, useEffect, useCallback } from 'react';
import { parseBackendDate, getShortWeekdayName } from '../../utils/formatters';
import { colors } from '../../styles/globalStyles';

export const ModalMeusAgendamentos = ({ isOpen, onClose, createApi, onCancelado }) => {
  const [agendamentos, setAgendamentos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmando, setConfirmando] = useState(null);
  const [cancelando, setCancelando] = useState(null);
  const [erro, setErro] = useState(null);

  const fetchAgendamentos = useCallback(async () => {
    if (!isOpen) return;
    setLoading(true);
    setErro(null);
    try {
      const api = createApi();
      const res = await api.get('/agendamento');
      setAgendamentos(res.data || []);
    } catch (err) {
      setErro('Erro ao carregar agendamentos.');
    } finally {
      setLoading(false);
    }
  }, [isOpen, createApi]);

  useEffect(() => {
    fetchAgendamentos();
  }, [fetchAgendamentos]);

  const handleCancelar = async (agendamentoId) => {
    setCancelando(agendamentoId);
    setErro(null);
    try {
      const api = createApi();
      await api.delete(`/agendamento/${agendamentoId}`);
      setConfirmando(null);
      await fetchAgendamentos();
      if (onCancelado) onCancelado();
    } catch (err) {
      const msg = err.response?.data?.error || err.message || 'Erro ao cancelar agendamento';
      setErro(msg);
    } finally {
      setCancelando(null);
    }
  };

  if (!isOpen) return null;

  // Usa início do dia como corte — aulas de hoje ficam em "futuros" até meia-noite
  const inicioDia = new Date();
  inicioDia.setHours(0, 0, 0, 0);
  const futuros = agendamentos.filter(a => parseBackendDate(a.data_hora) >= inicioDia);
  const passados = agendamentos.filter(a => parseBackendDate(a.data_hora) < inicioDia);

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.55)', zIndex: 9000,
          backdropFilter: 'blur(3px)'
        }}
      />

      <div style={{
        position: 'fixed', top: '50%', left: '50%',
        transform: 'translate(-50%, -50%)',
        backgroundColor: 'white', borderRadius: 16,
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
        zIndex: 9001,
        width: 'min(480px, calc(100vw - 2rem))',
        maxHeight: 'calc(100vh - 4rem)',
        display: 'flex', flexDirection: 'column',
        animation: 'slideIn 0.25s ease'
      }}>
        {/* Header */}
        <div style={{
          padding: '1.25rem 1.5rem',
          borderBottom: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
          borderRadius: '16px 16px 0 0'
        }}>
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700, color: 'white' }}>
              Meus Agendamentos
            </h2>
            <p style={{ margin: '0.2rem 0 0 0', fontSize: '0.78rem', color: 'rgba(255,255,255,0.8)' }}>
              {loading ? 'Carregando...' : `${futuros.length} aula${futuros.length !== 1 ? 's' : ''} agendada${futuros.length !== 1 ? 's' : ''}`}
            </p>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'rgba(255,255,255,0.15)', border: 'none', cursor: 'pointer',
              fontSize: '1.1rem', color: 'white', padding: '0.4rem 0.6rem',
              borderRadius: 8, lineHeight: 1
            }}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div style={{ overflowY: 'auto', flex: 1, padding: '1rem 1.25rem' }}>
          {loading && (
            <div style={{ textAlign: 'center', padding: '2.5rem', color: colors.gray }}>
              <div style={{
                width: 32, height: 32, border: '3px solid #e2e8f0',
                borderTopColor: colors.primary, borderRadius: '50%',
                animation: 'spin 1s linear infinite', margin: '0 auto 0.75rem'
              }} />
              Carregando seus agendamentos...
            </div>
          )}

          {!loading && erro && (
            <div style={{
              backgroundColor: '#fee2e2', color: '#991b1b', borderRadius: 10,
              padding: '0.875rem 1rem', marginBottom: '0.75rem',
              fontSize: '0.875rem', border: '1px solid #fecaca'
            }}>
              {erro}
            </div>
          )}

          {!loading && !erro && futuros.length === 0 && passados.length === 0 && (
            <div style={{ textAlign: 'center', padding: '2.5rem 1rem', color: colors.gray }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>📅</div>
              <div style={{ fontWeight: 600, fontSize: '0.95rem', marginBottom: '0.4rem', color: colors.dark }}>
                Nenhum agendamento ativo
              </div>
              <div style={{ fontSize: '0.825rem' }}>
                Você não possui aulas agendadas no momento.
              </div>
            </div>
          )}

          {!loading && futuros.length > 0 && (
            <div>
              <p style={{
                fontSize: '0.7rem', color: colors.gray, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                margin: '0 0 0.6rem 0'
              }}>
                Próximas aulas
              </p>
              {futuros.map(ag => {
                const data = parseBackendDate(ag.data_hora);
                const dia = getShortWeekdayName(data.getDay());
                const diaNum = String(data.getDate()).padStart(2, '0');
                const mesNum = String(data.getMonth() + 1).padStart(2, '0');
                const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
                const isConfirmando = confirmando === ag.id;
                const isCancelando = cancelando === ag.id;

                return (
                  <div
                    key={ag.id}
                    style={{
                      marginBottom: '0.625rem',
                      backgroundColor: isConfirmando ? '#fff7ed' : '#f0fdf4',
                      borderRadius: 12,
                      border: `1px solid ${isConfirmando ? '#fed7aa' : '#bbf7d0'}`,
                      overflow: 'hidden',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', padding: '0.875rem' }}>
                      <div style={{
                        width: 46, height: 46,
                        backgroundColor: isConfirmando ? '#f97316' : colors.primary,
                        borderRadius: 10, display: 'flex', flexDirection: 'column',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        transition: 'background-color 0.2s ease'
                      }}>
                        <span style={{ color: 'white', fontSize: 10, fontWeight: 600, lineHeight: 1 }}>{dia}</span>
                        <span style={{ color: 'white', fontSize: 17, fontWeight: 700, lineHeight: 1.1 }}>{diaNum}</span>
                        <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 10, lineHeight: 1 }}>/{mesNum}</span>
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontWeight: 600, color: colors.dark, fontSize: '0.9rem' }}>
                          {dia}, {diaNum}/{mesNum} às {hora}
                        </div>
                        {ag.descricao && (
                          <div style={{
                            fontSize: '0.775rem', color: colors.gray, marginTop: 2,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                            {ag.descricao}
                          </div>
                        )}
                      </div>
                      {!isConfirmando && (
                        <button
                          onClick={() => setConfirmando(ag.id)}
                          style={{
                            background: 'none', border: '1.5px solid #fca5a5',
                            color: '#dc2626', borderRadius: 8, padding: '0.4rem 0.75rem',
                            fontSize: '0.775rem', fontWeight: 600, cursor: 'pointer',
                            flexShrink: 0, whiteSpace: 'nowrap'
                          }}
                        >
                          Cancelar
                        </button>
                      )}
                    </div>

                    {isConfirmando && (
                      <div style={{
                        borderTop: '1px solid #fed7aa',
                        padding: '0.75rem 0.875rem',
                        backgroundColor: '#fff7ed'
                      }}>
                        <p style={{ margin: '0 0 0.6rem 0', fontSize: '0.825rem', color: '#92400e', fontWeight: 500 }}>
                          Tem certeza que deseja cancelar esta aula?
                        </p>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={() => setConfirmando(null)}
                            disabled={isCancelando}
                            style={{
                              flex: 1, background: 'white', border: '1.5px solid #d1d5db',
                              color: colors.gray, borderRadius: 8, padding: '0.45rem',
                              fontSize: '0.825rem', fontWeight: 600, cursor: 'pointer'
                            }}
                          >
                            Não, manter
                          </button>
                          <button
                            onClick={() => handleCancelar(ag.id)}
                            disabled={isCancelando}
                            style={{
                              flex: 1, background: '#dc2626', border: 'none',
                              color: 'white', borderRadius: 8, padding: '0.45rem',
                              fontSize: '0.825rem', fontWeight: 600, cursor: 'pointer',
                              opacity: isCancelando ? 0.7 : 1
                            }}
                          >
                            {isCancelando ? 'Cancelando...' : 'Sim, cancelar'}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {!loading && passados.length > 0 && (
            <div style={{ marginTop: futuros.length > 0 ? '1rem' : 0 }}>
              <p style={{
                fontSize: '0.7rem', color: colors.gray, fontWeight: 700,
                textTransform: 'uppercase', letterSpacing: '0.06em',
                margin: '0 0 0.6rem 0'
              }}>
                Realizadas recentemente
              </p>
              {passados.slice(0, 5).map(ag => {
                const data = parseBackendDate(ag.data_hora);
                const dia = getShortWeekdayName(data.getDay());
                const diaNum = String(data.getDate()).padStart(2, '0');
                const mesNum = String(data.getMonth() + 1).padStart(2, '0');
                const hora = data.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

                return (
                  <div
                    key={ag.id}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '0.75rem',
                      padding: '0.625rem 0.875rem', marginBottom: '0.5rem',
                      backgroundColor: '#f8fafc', borderRadius: 10,
                      border: '1px solid #e2e8f0', opacity: 0.6
                    }}
                  >
                    <div style={{ fontSize: '1rem' }}>✅</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '0.825rem', color: colors.gray, fontWeight: 500 }}>
                        {dia}, {diaNum}/{mesNum} às {hora}
                      </div>
                      {ag.descricao && (
                        <div style={{ fontSize: '0.75rem', color: colors.gray, marginTop: 1 }}>
                          {ag.descricao}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '0.875rem 1.25rem',
          borderTop: '1px solid #e2e8f0',
          display: 'flex', justifyContent: 'flex-end'
        }}>
          <button
            onClick={onClose}
            style={{
              background: colors.primary, color: 'white', border: 'none',
              borderRadius: 10, padding: '0.6rem 1.5rem',
              fontSize: '0.875rem', fontWeight: 600, cursor: 'pointer'
            }}
          >
            Fechar
          </button>
        </div>
      </div>
    </>
  );
};
